import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Trash2, Play, Pause, RotateCcw, Send, Loader2, AlertCircle } from 'lucide-react';
import api from '../services/api';
import socket from '../services/socket';
import { toast } from 'react-hot-toast';

export const RECORDER_STATES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PREVIEW: 'preview',
  SENDING: 'sending',
  SENT: 'sent'
};

const formatTime = (totalSeconds) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default function VoiceNoteRecorder({
  conversationId,
  recipientId,
  onVoiceSent,
  onStateChange,
  disabled = false
}) {
  const [recorderState, setRecorderState] = useState(RECORDER_STATES.IDLE);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
  const [permissionError, setPermissionError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewAudioRef = useRef(null);
  const recordedBlobRef = useRef(null);

  // Notify parent of state changes (e.g. to hide standard text input during recording/preview)
  useEffect(() => {
    onStateChange?.(recorderState);
  }, [recorderState, onStateChange]);

  // Clean up resources on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      if (previewAudioUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(previewAudioUrl);
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [previewAudioUrl]);

  // Handle preview audio element events
  useEffect(() => {
    if (!previewAudioUrl) return;

    const audio = new Audio(previewAudioUrl);
    previewAudioRef.current = audio;

    const handleTimeUpdate = () => {
      setPreviewCurrentTime(Math.floor(audio.currentTime));
    };
    const handleEnded = () => {
      setIsPlayingPreview(false);
      setPreviewCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [previewAudioUrl]);

  // 1. Start Recording (IDLE -> RECORDING)
  const startRecording = async () => {
    setPermissionError(null);

    // Browser feature check
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      const err = 'Voice recording is not supported in this browser.';
      setPermissionError(err);
      toast.error(err);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      // Determine supported mime type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';

      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || 'audio/webm'
        });
        recordedBlobRef.current = audioBlob;
        const objectUrl = URL.createObjectURL(audioBlob);
        setPreviewAudioUrl(objectUrl);
        setRecorderState(RECORDER_STATES.PREVIEW);
      };

      recorder.start(200); // 200ms time slices
      setRecorderState(RECORDER_STATES.RECORDING);
      setRecordingDuration(0);

      // Start recording timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Microphone access error:', err);
      let userMsg = 'Could not access microphone.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        userMsg = 'Microphone permission was denied. Please allow microphone access in your browser settings to record voice messages.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        userMsg = 'No microphone device was found on your system.';
      }
      setPermissionError(userMsg);
    }
  };

  // 2. Stop Recording (RECORDING -> PREVIEW)
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
  };

  // 3. Cancel Recording (RECORDING / PREVIEW -> IDLE)
  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (_) {}
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (previewAudioUrl) {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(previewAudioUrl);
      }
      setPreviewAudioUrl(null);
    }
    setIsPlayingPreview(false);
    setPreviewCurrentTime(0);
    setRecordingDuration(0);
    recordedBlobRef.current = null;
    setRecorderState(RECORDER_STATES.IDLE);
  }, [previewAudioUrl]);

  // 4. Toggle Preview Playback
  const togglePreviewPlayback = () => {
    if (!previewAudioRef.current) return;
    if (isPlayingPreview) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      previewAudioRef.current.play().then(() => {
        setIsPlayingPreview(true);
      }).catch(err => {
        console.error('Preview playback failed:', err);
      });
    }
  };

  // 5. Send Voice Message (PREVIEW -> SENDING -> SENT -> IDLE)
  const sendVoiceMessage = async () => {
    if (!recordedBlobRef.current) {
      toast.error('No audio recorded');
      cancelRecording();
      return;
    }

    if (!conversationId) {
      toast.error('No active conversation selected');
      return;
    }

    setRecorderState(RECORDER_STATES.SENDING);

    try {
      // Step A: Upload to /api/voice/upload (matches gigship-go's FormData upload)
      const formData = new FormData();
      const fileExt = recordedBlobRef.current.type.includes('mp4') ? 'mp4' : 'webm';
      formData.append('file', recordedBlobRef.current, `voice_note_${Date.now()}.${fileExt}`);

      const uploadResponse = await api.post('/voice/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { url: audioUrl, duration: backendDuration } = uploadResponse.data || {};

      if (!audioUrl) {
        throw new Error('Upload response missing audio URL');
      }

      const finalDuration = backendDuration || recordingDuration;

      // Step B: Emit send_message via socket with contentType 'voice'
      const socketPayload = {
        conversationId,
        recipientId,
        type: 'voice',
        contentType: 'voice',
        audioUrl,
        duration: finalDuration,
        content: {
          text: '[Voice Message]',
          type: 'voice',
          url: audioUrl,
          duration: finalDuration
        }
      };

      socket.emit('send_message', socketPayload);

      // Step C: Inform parent callback (for optimistic local message update)
      onVoiceSent?.({
        conversationId,
        recipientId,
        audioUrl,
        duration: finalDuration,
        createdAt: new Date().toISOString()
      });

      setRecorderState(RECORDER_STATES.SENT);
      toast.success('Voice note sent');

      // Cleanup and return to idle
      setTimeout(() => {
        cancelRecording();
      }, 500);

    } catch (error) {
      console.error('Failed to send voice message:', error);
      const errMsg = error.response?.data?.message || error.message || 'Failed to upload voice message';
      toast.error(errMsg);
      setRecorderState(RECORDER_STATES.PREVIEW);
    }
  };

  // ── Render Graceful Permission Denied Error State ─────────────────────────
  if (permissionError) {
    return (
      <div
        data-testid="mic-permission-error"
        className="flex items-center space-x-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg"
      >
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="flex-1">{permissionError}</span>
        <button
          type="button"
          onClick={() => setPermissionError(null)}
          className="text-amber-600 hover:text-amber-800 p-0.5 rounded"
          title="Dismiss"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // ── State 1: IDLE ──────────────────────────────────────────────────────────
  if (recorderState === RECORDER_STATES.IDLE) {
    return (
      <button
        type="button"
        onClick={startRecording}
        disabled={disabled}
        className="p-2 text-gray-600 hover:bg-gray-100 hover:text-blue-600 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Record voice note"
        aria-label="Record voice note"
        data-testid="mic-record-btn"
      >
        <Mic className="w-5 h-5" />
      </button>
    );
  }

  // ── State 2: RECORDING ─────────────────────────────────────────────────────
  if (recorderState === RECORDER_STATES.RECORDING) {
    return (
      <div
        data-testid="recording-bar"
        className="flex-1 flex items-center justify-between bg-red-50 border border-red-200 rounded-full px-4 py-2 text-red-700 animate-pulse"
      >
        <div className="flex items-center space-x-2">
          <span className="w-3 h-3 bg-red-600 rounded-full animate-ping" />
          <span className="text-sm font-semibold tracking-wider font-mono">
            {formatTime(recordingDuration)}
          </span>
          <span className="text-xs text-red-500 font-medium">Recording...</span>
        </div>

        <div className="flex items-center space-x-2">
          {/* Cancel / Trash */}
          <button
            type="button"
            onClick={cancelRecording}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors"
            title="Cancel recording"
            aria-label="Cancel recording"
            data-testid="cancel-record-btn"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          {/* Stop / Finish recording */}
          <button
            type="button"
            onClick={stopRecording}
            className="p-1.5 bg-red-600 text-white hover:bg-red-700 rounded-full shadow-sm transition-colors"
            title="Stop recording"
            aria-label="Stop recording"
            data-testid="stop-record-btn"
          >
            <Square className="w-4 h-4 fill-current" />
          </button>
        </div>
      </div>
    );
  }

  // ── State 3 & 4: PREVIEW / SENDING / SENT ──────────────────────────────────
  return (
    <div
      data-testid="preview-bar"
      className="flex-1 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-full px-4 py-2 text-blue-900"
    >
      <div className="flex items-center space-x-3">
        {/* Play/Pause Preview */}
        <button
          type="button"
          onClick={togglePreviewPlayback}
          disabled={recorderState === RECORDER_STATES.SENDING}
          className="p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50"
          title={isPlayingPreview ? 'Pause preview' : 'Play preview'}
          aria-label={isPlayingPreview ? 'Pause preview' : 'Play preview'}
          data-testid="preview-play-btn"
        >
          {isPlayingPreview ? (
            <Pause className="w-4 h-4 fill-current" />
          ) : (
            <Play className="w-4 h-4 fill-current ml-0.5" />
          )}
        </button>

        {/* Playback time indicator */}
        <div className="flex items-center space-x-1 font-mono text-xs text-blue-800">
          <span>{formatTime(isPlayingPreview ? previewCurrentTime : recordingDuration)}</span>
          <span>/</span>
          <span>{formatTime(recordingDuration)}</span>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        {/* Discard / Re-record button */}
        <button
          type="button"
          onClick={cancelRecording}
          disabled={recorderState === RECORDER_STATES.SENDING}
          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
          title="Discard and re-record"
          aria-label="Discard recording"
          data-testid="re-record-btn"
        >
          <Trash2 className="w-4 h-4" />
        </button>

        {/* Send voice message button */}
        <button
          type="button"
          onClick={sendVoiceMessage}
          disabled={recorderState === RECORDER_STATES.SENDING}
          className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-sm transition-colors text-xs font-semibold disabled:opacity-50"
          title="Send voice note"
          aria-label="Send voice note"
          data-testid="send-voice-btn"
        >
          {recorderState === RECORDER_STATES.SENDING ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Send className="w-3.5 h-3.5" />
              <span>Send</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
